/**
 * CoverSlidePreview
 *
 * Renders a live, scaled 16:9 cover slide preview using the exact same CoverSlide
 * component and CSS rendering path as the real DeckViewer / report cover.
 *
 * Logo & color are rendered as direct overlays inside the scaled 1280×720
 * container so they update synchronously when props change — no async
 * indirection or reliance on CoverSlide's internal useBrandOptional fallback.
 *
 * Why deck-scope brand tokens instead of a scoped CSS variable:
 *   The SVG cover backgrounds are dark-purple gradients. When hue-rotated to match
 *   a blue or navy brand color they become indistinguishable from the --ds-navy-dark
 *   (#0F1B2D) slide background — making the right-panel image invisible.
 *   The only way to make the cover look correct (matching the real report) is to
 *   write the deck token bridge on document.documentElement, which is exactly what
 *   DeckViewer and InternalDeckRenderPage do for slide rendering. A scoped
 *   --brand-hue-rotate on a wrapper div is not sufficient.
 *
 * Safety:
 *   - Deck-scope token writes use the shared restore disposer on prop changes/unmount.
 *   - During brand step (full-screen onboarding overlay), the global CSS change is
 *     invisible to any UI behind the overlay.
 *
 * Other notes:
 *   - getSlideComponent('cover') is used directly (no SlideRenderer) to bypass
 *     the validateSlideData gate on the minimal dummy payload.
 *   - ResizeObserver scales the 1280×720 slide to fit the available container width.
 */

import { useState, useEffect, useRef, useMemo } from 'react'
import type { CSSProperties } from 'react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/lib/config/auth-provider'
import { useTheme } from '@/lib/theme-provider'
import { useLogoNeedsInversion } from '@/lib/hooks/use-logo-needs-inversion'
import { applyThemeBrandTokens } from '@digital-twins/deck-ui/foundations/theme/applyThemeBrandTokens'
import {
  getSlideComponent,
  ensureSlideRegistryLoaded,
} from '@digital-twins/deck-ui/slides/registry'
import { resolveCoverBackgroundUrl } from '@digital-twins/deck-ui/foundations/cover_backgrounds'

const SLIDE_WIDTH = 1280
const SLIDE_HEIGHT = 720

interface CoverSlidePreviewProps {
  /** Live logo URL (Object URL from file input or remote URL from Brandfetch). */
  logoUrl: string | null
  /**
   * Server-measured inversion flag for a *saved* logo. Omit while the logo is
   * a freshly staged Object URL: the hook reads those pixels directly, which is
   * more current than any persisted measurement.
   */
  logoNeedsInversion?: boolean | null
  /** Live primary color as hex string, e.g. "#2563EB". Null = no override. */
  primaryColor: string | null
  /** Used for the cover title and as a stable seed for background selection. */
  orgName: string
  /** Effective heading font CSS stack. Scoped to the preview so font changes show live before they are saved globally. */
  headingFontFamily?: string | null
  /** Effective body font CSS stack. Scoped to the preview only. */
  bodyFontFamily?: string | null
  className?: string
}

/**
 * Build a scoped style that overrides the slide font tokens for this preview
 * only. Slides read `--ds-font-display` / `--ds-font-sans`, which bridge from
 * `--font-display` / `--font-geist-sans`; setting all four keeps the preview
 * in sync with pending font selections without mutating the document root.
 */
function buildFontVars(
  headingFontFamily: string | null | undefined,
  bodyFontFamily: string | null | undefined,
): CSSProperties {
  const vars: Record<string, string> = {}
  if (headingFontFamily) {
    vars['--ds-font-display'] = headingFontFamily
    vars['--font-display'] = headingFontFamily
  }
  if (bodyFontFamily) {
    vars['--ds-font-sans'] = bodyFontFamily
    vars['--font-geist-sans'] = bodyFontFamily
  }
  return vars as CSSProperties
}

export function CoverSlidePreview({
  logoUrl,
  logoNeedsInversion,
  primaryColor,
  orgName,
  headingFontFamily,
  bodyFontFamily,
  className,
}: CoverSlidePreviewProps) {
  const [registryReady, setRegistryReady] = useState(false)
  const viewportRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const logoNeedsFilter = useLogoNeedsInversion(logoUrl, logoNeedsInversion)
  const { user } = useAuth()

  const todayFormatted = useMemo(
    () => new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    [],
  )

  const { resolvedTheme } = useTheme()

  // Apply the selected primary color globally so the CoverSlide renders with
  // the exact same deck CSS vars as the real DeckViewer — including
  // --brand-hue-rotate which shifts the SVG background to the brand's color range.
  useEffect(() => {
    if (!primaryColor) return
    return applyThemeBrandTokens(
      {
        brandPrimaryColor: primaryColor,
        brandSecondaryColor: null,
        brandLogoUrl: null,
        brandLogoUrlOnDark: null,
        brandDomain: null,
        brandRoleGraph: null,
        brandRoleGraphVersion: null,
        brandRoleGraphHash: null,
      },
      resolvedTheme,
      { scope: 'deck', restore: true },
    )
  }, [primaryColor, resolvedTheme])

  useEffect(() => {
    let cancelled = false
    ensureSlideRegistryLoaded().then(() => {
      if (!cancelled) setRegistryReady(true)
    })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const viewport = viewportRef.current
    if (!viewport) return

    const update = () => {
      const { clientWidth } = viewport
      if (clientWidth > 0) setScale(clientWidth / SLIDE_WIDTH)
    }

    update()
    const observer = new ResizeObserver(update)
    observer.observe(viewport)
    return () => observer.disconnect()
  }, [registryReady])

  const heroImage = resolveCoverBackgroundUrl(undefined, orgName || 'preview')
  const createdByName = user?.name ?? 'You'

  // Suppress CoverSlide's internal logo rendering — empty string passes the
  // nullish coalesce (`??`) but is falsy so the `{resolvedLogoUrl && ...}`
  // guard inside CoverSlide skips its own <img>. We render the logo as a
  // direct overlay below instead, so it reacts instantly to prop changes.
  const dummyProps = {
    type: 'cover',
    title: 'What your audience really thinks',
    subtitle: 'Insights from simulated audiences across your target segments',
    heroImage,
    eyebrow: '',
    actionTitle: null,
    footerText: '',
    clientLogoUrl: '',
    authorName: createdByName,
    meta: {
      date: todayFormatted,
    },
  }

  if (!registryReady) {
    return (
      <div
        className={cn('w-full aspect-video rounded-lg bg-muted animate-pulse', className)}
        data-testid="brand-step-cover-preview"
        aria-label="Loading cover preview"
      />
    )
  }

  const CoverComponent = getSlideComponent('cover')
  if (!CoverComponent) {
    return (
      <div
        className={cn('w-full aspect-video rounded-lg bg-muted flex items-center justify-center', className)}
        data-testid="brand-step-cover-preview"
      >
        <span className="text-muted-foreground text-sm">Preview unavailable</span>
      </div>
    )
  }

  return (
    <div
      ref={viewportRef}
      className={cn('relative w-full overflow-hidden rounded-lg', className)}
      style={{ aspectRatio: '16 / 9', pointerEvents: 'none' }}
      data-testid="brand-step-cover-preview"
      aria-label="Cover slide preview"
    >
      {/* Fixed 1280×720 inner, scaled to fit the container via ResizeObserver */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: SLIDE_WIDTH,
          height: SLIDE_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          ...buildFontVars(headingFontFamily, bodyFontFamily),
        }}
      >
        <CoverComponent {...dummyProps} />

        {/* Direct logo overlay — uses logoUrl prop synchronously so changes
            are reflected immediately without async blob conversion. Matches
            the exact positioning CoverSlide uses internally. */}
        {logoUrl && (
          <img
            src={logoUrl}
            alt="Brand logo preview"
            referrerPolicy="no-referrer"
            style={{
              position: 'absolute',
              top: 30,
              right: 40,
              height: 'calc(var(--ds-cover-logo-h, 60px) * 0.64)',
              width: 'auto',
              objectFit: 'contain',
              zIndex: 10,
              filter: logoNeedsFilter ? 'brightness(0) invert(1)' : undefined,
            }}
          />
        )}
      </div>
    </div>
  )
}
